import { gql } from '@apollo/client';

// ==================== CREATIVE QUERIES ====================

export const GET_CREATIVE = gql`
  query GetCreative($id: ID!) {
    getCreative(id: $id) {
      id
      userID
      creativeType
      visualTypes
      status
      projectCount
      packageCount
      canBePublished
      profilePicture
      displayName
      jobTitle
      description
      skills
      averageRating
      reviewCount
      viewCount
      user {
        id
        firstName
        lastName
        profilePicture
      }
      packages {
        items {
          id
          creativeID
          serviceType
          name
          description
          packageType
          duration
          price
          pricePerHour
          deliverables
          includedServices
          deliveryTime
          portfolioImages
          equipment
          isPopular
          order
          bookingCount
          averageRating
          isActive
          status
          createdAt
          updatedAt
        }
        nextToken
      }
      projects {
        items {
          id
          creativeID
          title
          description
          images {
            url
            caption
            thumbnailUrl
            order
          }
          audios {
            url
            title
            artists
            genre
            coverImage
            order
            durationMs
          }
          category
          tags
          status
          viewCount
          studioID
          studio {
            id
            StudioName
            images
            studioLocation
            city
            country
          }
          createdAt
          updatedAt
        }
        nextToken
      }
      studios {
        items {
          id
          studioID
          creativeID
          studio {
            id
            StudioName
            images
            studioLocation
            city
            country
          }
          createdAt
          updatedAt
        }
        nextToken
      }
      createdAt
      updatedAt
    }
  }
`;

// Public query for MemberPage - doesn't include sensitive user fields
export const GET_CREATIVE_PUBLIC = gql`
  query GetCreativePublic($id: ID!) {
    getCreative(id: $id) {
      id
      userID
      creativeType
      visualTypes
      status
      projectCount
      packageCount
      profilePicture
      displayName
      jobTitle
      description
      skills
      averageRating
      reviewCount
      viewCount
      user {
        id
        firstName
        lastName
        profilePicture
      }
      packages {
        items {
          id
          creativeID
          serviceType
          name
          description
          packageType
          duration
          price
          pricePerHour
          deliverables
          includedServices
          deliveryTime
          portfolioImages
          equipment
          isPopular
          order
          bookingCount
          averageRating
          isActive
          status
          createdAt
          updatedAt
        }
        nextToken
      }
      projects {
        items {
          id
          creativeID
          title
          description
          images {
            url
            caption
            thumbnailUrl
            order
          }
          audios {
            url
            title
            artists
            genre
            coverImage
            order
            durationMs
          }
          category
          tags
          status
          viewCount
          studioID
          studio {
            id
            StudioName
            images
            studioLocation
            city
            country
          }
          createdAt
          updatedAt
        }
        nextToken
      }
      studios {
        items {
          id
          studioID
          creativeID
          studio {
            id
            StudioName
            images
            studioLocation
            city
            country
          }
          createdAt
          updatedAt
        }
        nextToken
      }
      createdAt
      updatedAt
    }
  }
`;

export const CREATIVES_BY_USER_ID = gql`
  query CreativesByUserID(
    $userID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelCreativeFilterInput
    $limit: Int
    $nextToken: String
  ) {
    creativesByUserID(
      userID: $userID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        userID
        creativeType
        visualTypes
        status
        projectCount
        packageCount
        canBePublished
        profilePicture
        displayName
        jobTitle
        description
        skills
        averageRating
        reviewCount
        viewCount
        user {
          id
          firstName
          lastName
          profilePicture
        }
        packages {
          items {
            id
            name
            packageType
            price
            isActive
            status
          }
          nextToken
        }
        projects {
          items {
            id
            title
            status
            viewCount
            images {
              url
              caption
              thumbnailUrl
              order
            }
            audios {
              url
              title
              artists
              genre
              coverImage
              order
              durationMs
            }
          }
          nextToken
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const LIST_CREATIVES = gql`
  query ListCreatives(
    $filter: ModelCreativeFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listCreatives(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        userID
        creativeType
        visualTypes
        status
        projectCount
        packageCount
        canBePublished
        profilePicture
        displayName
        jobTitle
        description
        skills
        averageRating
        reviewCount
        viewCount
        user {
          id
          firstName
          lastName
          profilePicture
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const HAS_CREATIVE = gql`
  query HasCreative($userID: ID!) {
    creativesByUserID(userID: $userID, limit: 1) {
      items {
        id
      }
    }
  }
`;

// ==================== CREATIVE PACKAGE QUERIES ====================

export const GET_CREATIVE_PACKAGE = gql`
  query GetCreativePackage($id: ID!) {
    getCreativePackage(id: $id) {
      id
      creativeID
      serviceType
      name
      description
      packageType
      duration
      price
      pricePerHour
      deliverables
      includedServices
      deliveryTime
      portfolioImages
      equipment
      isPopular
      order
      bookingCount
      averageRating
      isActive
      status
      creative {
        id
        displayName
        profilePicture
        creativeType
        visualTypes
        userID
        user {
          id
          firstName
          lastName
          profilePicture
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_CREATIVE_PACKAGE_BOOKING_DETAILS = gql`
  query getCreativePackage($id: ID!) {
    getCreativePackage(id: $id) {
      id
      creativeID
      name
      description
      pricePerHour
      deliverables
      portfolioImages
    }
  }
`;

export const GET_CREATIVE_IMAGE = gql`
  query GetCreativeImage($id: ID!) {
    getCreativePackage(id: $id) {
      creative {
        id
        displayName
        profilePicture
        creativeType
        visualTypes
        userID
        user {
          id
          firstName
          lastName
          profilePicture
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_CREATIVE_PACKAGE_IMAGE = gql`
  query GetCreativePackageImage($id: ID!) {
    getCreativePackage(id: $id) {
      id
      name
      portfolioImages
    }
  }
`;

export const CREATIVE_PACKAGES_BY_CREATIVE_ID = gql`
  query CreativePackagesByCreativeID(
    $creativeID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelCreativePackageFilterInput
    $limit: Int
    $nextToken: String
  ) {
    creativePackagesByCreativeID(
      creativeID: $creativeID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        creativeID
        serviceType
        name
        description
        packageType
        duration
        price
        pricePerHour
        deliverables
        includedServices
        deliveryTime
        portfolioImages
        equipment
        isPopular
        order
        bookingCount
        averageRating
        isActive
        status
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const LIST_CREATIVE_PACKAGES = gql`
  query ListCreativePackages(
    $filter: ModelCreativePackageFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listCreativePackages(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        creativeID
        serviceType
        name
        description
        packageType
        duration
        price
        pricePerHour
        deliverables
        includedServices
        deliveryTime
        portfolioImages
        equipment
        isPopular
        order
        bookingCount
        averageRating
        isActive
        status
        creative {
          id
          displayName
          profilePicture
          creativeType
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ==================== CREATIVE PROJECT QUERIES ====================

export const GET_CREATIVE_PROJECT = gql`
  query GetCreativeProject($id: ID!) {
    getCreativeProject(id: $id) {
      id
      creativeID
      title
      description
      images {
        url
        caption
        thumbnailUrl
        order
      }
      audios {
        url
        title
        artists
        genre
        coverImage
        order
        durationMs
      }
      category
      tags
      status
      viewCount
      studioID
      creative {
        id
        displayName
        profilePicture
        creativeType
        visualTypes
        userID
        user {
          id
          firstName
          lastName
          profilePicture
        }
      }
      studio {
        id
        StudioName
        images
        studioLocation
        city
        country
        User {
          id
          firstName
          lastName
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const CREATIVE_PROJECTS_BY_CREATIVE_ID = gql`
  query CreativeProjectsByCreativeID(
    $creativeID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelCreativeProjectFilterInput
    $limit: Int
    $nextToken: String
  ) {
    creativeProjectsByCreativeID(
      creativeID: $creativeID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        creativeID
        title
        description
        images
        audios
        category
        tags
        status
        viewCount
        studioID
        studio {
          id
          StudioName
          images
          studioLocation
          city
          country
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const CREATIVE_PROJECTS_BY_STUDIO_ID = gql`
  query CreativeProjectsByStudioID(
    $studioID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelCreativeProjectFilterInput
    $limit: Int
    $nextToken: String
  ) {
    creativeProjectsByStudioID(
      studioID: $studioID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        creativeID
        title
        description
        images
        audios
        category
        tags
        status
        viewCount
        creative {
          id
          displayName
          profilePicture
          creativeType
          visualTypes
          userID
          user {
            id
            firstName
            lastName
            profilePicture
          }
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const LIST_CREATIVE_PROJECTS = gql`
  query ListCreativeProjects(
    $filter: ModelCreativeProjectFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listCreativeProjects(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        creativeID
        title
        description
        images
        audios
        category
        tags
        status
        viewCount
        studioID
        creative {
          id
          displayName
          profilePicture
          creativeType
        }
        studio {
          id
          StudioName
          images
          studioLocation
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ==================== STUDIO CREATIVE QUERIES ====================

export const GET_STUDIO_CREATIVE = gql`
  query GetStudioCreative($id: ID!) {
    getStudioCreative(id: $id) {
      id
      studioID
      creativeID
      studio {
        id
        StudioName
        images
        studioLocation
        city
        country
        User {
          id
          firstName
          lastName
          profilePicture
        }
      }
      creative {
        id
        displayName
        profilePicture
        jobTitle
        description
        creativeType
        visualTypes
        averageRating
        reviewCount
        userID
        user {
          id
          firstName
          lastName
          profilePicture
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const STUDIO_CREATIVES_BY_STUDIO_ID = gql`
  query StudioCreativesByStudioID(
    $studioID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelStudioCreativeFilterInput
    $limit: Int
    $nextToken: String
  ) {
    studioCreativesByStudioID(
      studioID: $studioID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        studioID
        creativeID
        creative {
          id
          displayName
          profilePicture
          jobTitle
          description
          creativeType
          visualTypes
          status
          skills
          averageRating
          reviewCount
          projectCount       
          packageCount       
          userID
          user {
            id
            firstName
            lastName
            profilePicture
          }
          packages {         
            items {
              id
              name
              description
              duration
              price
              pricePerHour
              includedServices
              portfolioImages  
              packageType
              isActive
            }
            nextToken
          }
          projects {
            items {
              id
              description
              title
              status
              viewCount
              images {
                url
                caption
                thumbnailUrl
                order
              }
              audios {
                url
                title
                artists
                genre
                coverImage
                order
                durationMs
              }
            }
            nextToken
          }
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const STUDIO_CREATIVES_BY_CREATIVE_ID = gql`
  query StudioCreativesByCreativeID(
    $creativeID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelStudioCreativeFilterInput
    $limit: Int
    $nextToken: String
  ) {
    studioCreativesByCreativeID(
      creativeID: $creativeID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        studioID
        creativeID
        studio {
          id
          StudioName
          images
          studioLocation
          city
          country
          User {
            id
            firstName
            lastName
            profilePicture
          }
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const LIST_STUDIO_CREATIVES = gql`
  query ListStudioCreatives(
    $filter: ModelStudioCreativeFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listStudioCreatives(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        studioID
        creativeID
        studio {
          id
          StudioName
          images
          studioLocation
        }
        creative {
          id
          displayName
          profilePicture
          creativeType
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;
